/**
 * Authentication Hook
 *
 * Provides authentication state and methods for the Compass web app.
 * Supports both embedded (iframe) mode and standalone development mode.
 *
 * In embedded mode: Uses the auth bridge to receive tokens from parent
 * In standalone mode: Uses VITE_DEV_AUTH_TOKEN env var or returns null
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { isEmbedded } from '@/lib/iframe/types';
import {
  initAuthBridge,
  destroyAuthBridge,
  getAuthToken,
  requestToken as bridgeRequestToken,
  subscribeToToken,
  isAuthBridgeInitialized,
} from '@/lib/iframe/auth-bridge';

// ============================================================================
// Types
// ============================================================================

export interface UseAuthReturn {
  /** Current auth token, null if not authenticated */
  token: string | null;
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether we're running in embedded mode */
  isEmbedded: boolean;
  /** Request a fresh token (only works in embedded mode) */
  requestToken: () => void;
  /** Whether the auth state is still being initialized */
  isLoading: boolean;
}

// ============================================================================
// Development Token
// ============================================================================

/**
 * Get development auth token from environment
 * Only used in standalone (non-embedded) mode
 */
const getDevToken = (): string | null => {
  // Only use dev token in development mode and when not embedded
  if (import.meta.env.PROD || isEmbedded()) {
    return null;
  }

  const devToken = import.meta.env.VITE_DEV_AUTH_TOKEN;
  if (devToken && typeof devToken === 'string' && devToken.trim()) {
    return devToken.trim();
  }

  return null;
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Authentication hook for Compass web app
 *
 * @example
 * ```tsx
 * function ProtectedComponent() {
 *   const { token, isAuthenticated, isLoading } = useAuth();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (!isAuthenticated) {
 *     return <AuthRequired />;
 *   }
 *
 *   return <YourContent />;
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const embedded = useMemo(() => isEmbedded(), []);
  const [token, setToken] = useState<string | null>(() => {
    // Initialize with dev token for standalone mode
    if (!embedded) {
      return getDevToken();
    }
    // For embedded mode, start with null until bridge provides token
    return isAuthBridgeInitialized() ? getAuthToken() : null;
  });
  const [isLoading, setIsLoading] = useState(() => embedded && !isAuthBridgeInitialized());

  // Initialize auth bridge for embedded mode
  useEffect(() => {
    // Standalone mode doesn't need any setup - already initialized with dev token
    if (!embedded) {
      return;
    }

    // Embedded mode - initialize auth bridge
    initAuthBridge();

    // Subscribe to token changes
    const unsubscribe = subscribeToToken((newToken) => {
      setToken(newToken);
      setIsLoading(false);
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
      destroyAuthBridge();
    };
  }, [embedded]);

  // Request token callback
  const requestToken = useCallback(() => {
    if (embedded) {
      bridgeRequestToken();
    } else {
      console.debug('[useAuth] Cannot request token in standalone mode');
    }
  }, [embedded]);

  // Compute authentication state
  const isAuthenticated = token !== null;

  return {
    token,
    isAuthenticated,
    isEmbedded: embedded,
    requestToken,
    isLoading,
  };
}

/**
 * Get the current auth token synchronously
 * Useful for non-React contexts like API interceptors
 *
 * @returns Current token or null
 */
export function getToken(): string | null {
  if (!isEmbedded()) {
    return getDevToken();
  }
  return getAuthToken();
}

export default useAuth;
